import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import {
    CheckCircleIcon,
    AlertCircleIcon,
    ArrowRightIcon,
    LightbulbIcon,
    SparklesIcon,
} from "lucide-react";
import { ImprovementItem, LackingItem, MatchItem } from "@type/job";
import CandidateEmptyInsight from "./candidate-job-insight-empty";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@component/select";


interface CandidateInsightsProps {
    isLoading: boolean
    matches: MatchItem[];
    lackingAreas: LackingItem[];
    improvements: ImprovementItem[];
}

export default function CandidateInsights({
    isLoading = false,
    matches,
    lackingAreas,
    improvements,
}: CandidateInsightsProps) {
    const [activeTab, setActiveTab] = useState("matches");

    const tabOptions = [
        {
            value: "matches",
            label: "Required Skills Met",
            icon: <CheckCircleIcon className="h-4 w-4 text-green-500" />,
        },
        {
            value: "lacking",
            label: "Missing Requirements",
            icon: <AlertCircleIcon className="h-4 w-4 text-amber-500" />,
        },
        {
            value: "improvements",
            label: "Improvement Plan",
            icon: <ArrowRightIcon className="h-4 w-4 text-blue-500" />,
        },
    ];

    return (
        <div className="space-y-6">
            <h2 className="text-lg font-semibold flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-blue-500" />
                Insights For You
            </h2>

            <Card className="shadow-sm">
                <CardHeader className="pb-0">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">Job Match Insights</CardTitle>
                        </div>

                        <div
                            className="text-sm text-muted-foreground"
                            style={{ paddingTop: "0px", paddingBottom: "10px" }}
                        >
                            Analysis of how your profile matches this job's specific
                            requirements.
                        </div>
                    </div>
                    <Tabs
                        defaultValue="matches"
                        value={activeTab}
                        onValueChange={setActiveTab}
                        className="w-full mt-2"
                    >
                        <TabsList className="hidden sm:grid w-full grid-cols-3">
                            <TabsTrigger value="matches" className="flex items-center gap-2">
                                <CheckCircleIcon className="h-4 w-4 text-green-500" />

                                <span>Required Skills Met</span>
                            </TabsTrigger>
                            <TabsTrigger value="lacking" className="flex items-center gap-2">
                                <AlertCircleIcon className="h-4 w-4 text-amber-500" />

                                <span>Missing Requirements</span>
                            </TabsTrigger>
                            <TabsTrigger
                                value="improvements"
                                className="flex items-center gap-2"
                            >
                                <ArrowRightIcon className="h-4 w-4 text-blue-500" />

                                <span>Improvement Plan</span>
                            </TabsTrigger>
                        </TabsList>

                        <div className="flex sm:hidden mb-4">
                            <Select value={activeTab} onValueChange={setActiveTab}>
                                <SelectTrigger className="w-full">
                                    <div className="flex items-center gap-2">
                                        {tabOptions.find((tab) => tab.value === activeTab)?.icon}
                                        <span>
                                            {tabOptions.find((tab) => tab.value === activeTab)?.label}
                                        </span>
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    {tabOptions.map((tab) => (
                                        <SelectItem key={tab.value} value={tab.value}>
                                            <div className="flex items-center gap-2">
                                                {tab.icon}
                                                <span>{tab.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <TabsContent value="matches" className="pt-4">
                            <div className="space-y-4">
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-4">
                                        <div className="flex space-x-2 mb-4">
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "0ms" }}
                                            ></div>
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "300ms" }}
                                            ></div>
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "600ms" }}
                                            ></div>
                                        </div>
                                        <p className="text-center text-sm font-medium mb-4">
                                            Generating your insights...
                                        </p>
                                    </div>
                                ) : (
                                    <>{matches.length > 0 ? matches.map((match, index) => (
                                        <div
                                            key={index}
                                            className="bg-green-50 rounded-lg p-4 border border-green-100"
                                            style={{ border: "1px solid #bbf7d0" }}
                                        >
                                            <div className="flex items-start gap-3">
                                                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />

                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-bold text-green-700">
                                                            {match.skill}
                                                        </span>
                                                    </div>

                                                    <p className="text-sm text-green-800">
                                                        {match.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <CandidateEmptyInsight type="matches" />
                                    )}
                                    </>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="lacking" className="pt-4">
                            <div className="space-y-4">
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-4">
                                        <div className="flex space-x-2 mb-4">
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "0ms" }}
                                            ></div>
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "300ms" }}
                                            ></div>
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "600ms" }}
                                            ></div>
                                        </div>
                                        <p className="text-center text-sm font-medium mb-4">
                                            Generating your insights...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {lackingAreas.length > 0 ? lackingAreas.map((area, index) => (
                                            <div
                                                key={index}
                                                className="bg-amber-50 rounded-lg p-4 border border-amber-100"
                                                style={{ border: "1px solid #fde68a" }}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <AlertCircleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />

                                                    <div className="space-y-2">
                                                        <div className="flex items-center">
                                                            <span className="font-bold text-amber-700">
                                                                {area.skill}
                                                            </span>
                                                        </div>

                                                        <p className="text-sm text-amber-800">
                                                            {area.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <CandidateEmptyInsight type="lacking" />
                                        )}
                                    </>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="improvements" className="pt-4">
                            <div className="space-y-4">
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-4">
                                        <div className="flex space-x-2 mb-4">
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "0ms" }}
                                            ></div>
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "300ms" }}
                                            ></div>
                                            <div
                                                className="h-3 w-3 bg-blue-500 rounded-full animate-bounce"
                                                style={{ animationDelay: "600ms" }}
                                            ></div>
                                        </div>
                                        <p className="text-center text-sm font-medium mb-4">
                                            Generating your insights...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {improvements.length > 0 ? improvements.map((improvement, index) => (
                                            <div
                                                key={index}
                                                className="bg-blue-50 rounded-lg p-4 border border-blue-100"
                                                style={{ border: "1px solid #bfdbfe" }}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <LightbulbIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />

                                                    <h3 className="font-bold text-blue-700 text-base">
                                                        {improvement.title}
                                                    </h3>
                                                </div>

                                                <div
                                                    className="mt-2 ml-8 space-y-3"
                                                    style={{ color: "#2563eb" }}
                                                >
                                                    <p
                                                        className="text-sm text-gray-600"
                                                        style={{ color: "#1d4ed8" }}
                                                    >
                                                        {improvement.description}
                                                    </p>
                                                    <div className="space-y-2">
                                                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                                                            {improvement.action_items.map((item, itemIndex) => (
                                                                <li key={itemIndex} style={{ color: "#1d4ed8" }}>
                                                                    {item}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <CandidateEmptyInsight type="improvements" />
                                        )}
                                    </>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </CardHeader>
                <CardContent></CardContent>
            </Card>
        </div>
    );
}
